/**
 * Interface language (MPPI-00012).
 *
 * Persisted per browser so the choice survives a reload, and applied to the
 * document root rather than only to React state — `dir` and `lang` on <html>
 * are what actually drive text direction, font selection, form control
 * alignment and screen-reader pronunciation. Setting a React variable alone
 * would translate words while leaving an Arabic interface laid out
 * left-to-right, which is worse than not translating at all.
 *
 * **Scope, stated honestly.** This ships the mechanism: the picker, the
 * persistence, the document direction, and the loader for locale bundles. It
 * does NOT ship a translated admin interface. The Arabic bundle carries only
 * strings that have been verified, because an admin console machine-translated
 * unattended produces confident nonsense in a tool staff rely on — and nobody
 * reading English would catch it. Untranslated keys fall back to English,
 * visibly rather than silently.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";

export type Language = "en" | "ar" | "vi";

export const LANGUAGES: { code: Language; label: string; native: string; rtl: boolean }[] = [
  { code: "en", label: "English", native: "English", rtl: false },
  { code: "ar", label: "Arabic", native: "العربية", rtl: true },
  // MPPI-00086. Vietnamese is left-to-right, so it needs no direction
  // handling — but it does need the Latin-Extended glyphs (ế, ệ, ữ) to
  // render, which the default stack covers.
  { code: "vi", label: "Vietnamese", native: "Tiếng Việt", rtl: false },
];

interface LanguageState {
  language: Language;
  setLanguage: (language: Language) => void;
}

/** Put direction and language on <html> where the browser can act on them. */
export function applyDocumentLanguage(language: Language): void {
  const meta = LANGUAGES.find((l) => l.code === language) ?? LANGUAGES[0];
  const root = document.documentElement;
  root.setAttribute("lang", meta.code);
  root.setAttribute("dir", meta.rtl ? "rtl" : "ltr");
}

export const useLanguageStore = create<LanguageState>()(
  persist(
    (set) => ({
      language: "en",
      setLanguage: (language) => {
        applyDocumentLanguage(language);
        set({ language });
      },
    }),
    {
      name: "mppi-admin-language",
      onRehydrateStorage: () => (state) => {
        // Re-apply on load: the persisted value is restored after first paint,
        // so without this a returning Arabic user gets an LTR flash and, if
        // nothing else triggers a set, an LTR session.
        if (state) applyDocumentLanguage(state.language);
      },
    },
  ),
);
